import { type KeywordTypeId } from "desktop/src/modules/OutreachContactSearch/constants";
import { kebabCase, orderBy } from "lodash-es";
import { computed, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { nl2br } from "shared/helpers/string";
import { type Phone } from "shared/resources/types";
import type { OutreachContactOperator } from "shared/stores/outreachContacts";
import { type Nullable } from "shared/types";
import {
  type BaseOutlet,
  type BasePrivateOutlet,
  type Influencer,
  type InfluencerAddress,
  type Outlet,
  type OutletAddress,
  type OutreachTopic,
  type PrivateInfluencer,
  type PrivateOutlet,
} from "shared/types/outreach";

export type ContactType =
  | Outlet
  | PrivateOutlet
  | Influencer
  | PrivateInfluencer;

export type ContactResourceType =
  | "influencer"
  | "outlet"
  | "private_influencer"
  | "private_outlet";

export type OutreachContactId = `${ContactResourceType}_${number}`;

export type SerializedKeywordRule =
  | { keyword: string; types: KeywordTypeId[] }
  | { outreach_topic_ids: number[] }
  | { physical_location_ids: number[] };

export interface SerializedKeywordSet {
  operator: OutreachContactOperator;
  rules: SerializedKeywordRule[];
}

export type ContactCategory = "influencer" | "outlet";

export interface ContactProps {
  contact?: ContactType;
}

export interface ContactResponse {
  contacts: ContactType[];
  influencer_count: Nullable<number>;
  outlet_count: Nullable<number>;
  private_influencer_count: Nullable<number>;
  private_outlet_count: Nullable<number>;
}

export function hasContactOptedOut(contact: Influencer) {
  return contact.opted_type?.replace(/[-\s]/g, "").toLowerCase() === "optedout";
}

export function isInfluencerContact(
  contact: ContactType
): contact is Influencer {
  return contact.type === "influencer";
}

export function isOutletContact(contact: ContactType): contact is Outlet {
  return contact.type === "outlet";
}

export function isPrivateOutletContact(
  contact: ContactType
): contact is PrivateOutlet {
  return contact.type === "private_outlet";
}

export function isPrivateInfluencerContact(
  contact: ContactType
): contact is PrivateInfluencer {
  return contact.type === "private_influencer";
}

export function isSocialContact(
  contact: ContactType
): contact is Outlet | Influencer {
  return "homepages" in contact && "socials" in contact;
}

function isValidContactResourceType(
  value: unknown
): value is ContactResourceType {
  return (
    value === "influencer" ||
    value === "outlet" ||
    value === "private_influencer" ||
    value === "private_outlet"
  );
}

export default function useContact(options: ContactProps) {
  const contact = computed<ContactType>(
    () => options.contact || ({} as ContactType)
  );

  const type = computed<ContactResourceType | "">(() => {
    const rawType = contact.value.type;

    return isValidContactResourceType(rawType) ? rawType : "";
  });

  const isOutlet = computed<boolean>(() => isOutletContact(contact.value));

  const contactOptedOut = computed<boolean>(
    () =>
      isInfluencerContact(contact.value) && hasContactOptedOut(contact.value)
  );

  const contactClass = computed<string[]>(() => {
    const classes = ["c-outreach-contact"];

    const outletType =
      (isOutletContact(contact.value) && contact.value.outlet_type) ||
      (isInfluencerContact(contact.value) && contact.value.outlet?.outlet_type);

    if (outletType) {
      classes.push(`c-outreach-contact-type-${kebabCase(outletType.name)}`);
    }

    return classes;
  });

  const contactTitle = computed<string>(() => {
    if (
      isOutletContact(contact.value) ||
      isInfluencerContact(contact.value) ||
      isPrivateOutletContact(contact.value)
    ) {
      return contact.value.name;
    }

    if (isPrivateInfluencerContact(contact.value)) {
      return `${contact.value.first_name} ${contact.value.last_name}`;
    }

    return "";
  });

  const contactLanguages = computed<string>(() => {
    if (isOutletContact(contact.value) || isInfluencerContact(contact.value)) {
      const localizedLanguages: string[] = [];

      (contact.value.languages ?? []).forEach((language) => {
        const localized = getLocaleText(
          `modals.outreach_languages_modal.languages.${language}`
        );

        if (localized) localizedLanguages.push(localized);
      });

      return localizedLanguages.join(", ");
    }

    return "";
  });

  const contactAddresses = computed<OutletAddress[] | InfluencerAddress[]>(
    () => {
      if (isOutletContact(contact.value)) {
        return contact.value.outlet_addresses || [];
      }

      if (isInfluencerContact(contact.value)) {
        return contact.value.influencer_addresses || [];
      }

      return [];
    }
  );

  const sortedContactAddresses = computed<
    OutletAddress[] | InfluencerAddress[]
  >(() => {
    if (type.value === "private_influencer") {
      return [];
    }

    const addresses = contactAddresses.value;

    return orderBy(addresses, "default_flag", "desc") as
      | OutletAddress[]
      | InfluencerAddress[];
  });

  const contactLocation = computed<string>(() => {
    if (!sortedContactAddresses.value.length) {
      if (
        isInfluencerContact(contact.value) ||
        isOutletContact(contact.value)
      ) {
        return contact.value.location;
      }

      return "";
    }

    const { address } = sortedContactAddresses.value[0];

    let location: string =
      isInfluencerContact(contact.value) || isOutletContact(contact.value)
        ? contact.value.location
        : "";

    if (address.country) {
      location = address.country;
    }

    if (address.city) {
      if (location || address.county || address.state) {
        location = `${address.city}, ${
          location || address.state || address.county
        }`;
      } else {
        location = address.city;
      }
    }

    // return and remove double commas
    return location.replace(/,+/g, ",");
  });

  const contactRole = computed<string>(() => {
    if (isPrivateInfluencerContact(contact.value)) {
      return contact.value.job_title ?? "";
    }

    if (isInfluencerContact(contact.value)) {
      return contact.value.business_card_titles?.join("/") || "";
    }

    return "";
  });

  const contactTopics = computed<OutreachTopic[]>(() => {
    if (isOutletContact(contact.value)) {
      return contact.value.outlet_outreach_topics || [];
    }

    if (isInfluencerContact(contact.value)) {
      return contact.value.influencer_outreach_topics;
    }

    return [];
  });

  const visibleContactTopics = computed(() => contactTopics.value.slice(0, 4));

  const moreContactTopics = computed(() =>
    contactTopics.value.slice(4, contactTopics.value.length)
  );

  const moreContactTopicsTooltip = computed(() =>
    moreContactTopics.value.map((topic) => topic.name).join(", ")
  );

  const showAllAddresses = ref<boolean>(false);

  const visibleContactAddresses = computed(() => {
    const addresses = showAllAddresses.value
      ? contactAddresses.value
      : contactAddresses.value.slice(0, 1);

    return addresses.map((address) => {
      const lines = [];
      if (address.address.addressLine1)
        lines.push(address.address.addressLine1);
      if (address.address.addressLine2)
        lines.push(address.address.addressLine2);
      if (address.address.city) lines.push(address.address.city);
      if (address.address.postalCode) lines.push(address.address.postalCode);

      if (
        address.address.state &&
        address.address.country !== "United Kingdom"
      ) {
        lines.push(address.address.state);
      }

      if (address.address.country) lines.push(address.address.country);

      return {
        ...address,
        text: lines.map((line) => line.replace(/,\s*$/, "")).join(", "),
      };
    });
  });

  const contactPrimaryOutletName = computed<string>(() => {
    if (isInfluencerContact(contact.value)) {
      return contact.value.outlet?.name ?? "";
    }

    return "";
  });

  const contactPrimaryOutletId = computed<number>(() => {
    if (isInfluencerContact(contact.value)) {
      return Number(contact.value.outlet?.id ?? 0);
    }

    return 0;
  });

  const contactPrimaryOutlet = computed(() => ({
    id: contactPrimaryOutletId.value,
    name: contactPrimaryOutletName.value,
  }));

  const contactCirculations = computed(() => {
    if (isOutletContact(contact.value)) {
      return contact.value.normalized_circulations || [];
    }

    return [];
  });

  const contactCirculationTooltip = computed<string>(() => {
    if (!contactCirculations.value.length) return "";

    return contactCirculations.value[0].circulationType.startsWith("uvpm")
      ? getLocaleText("outreach.contact.unique_viewers")
      : getLocaleText("outreach.contact.circulation");
  });

  const contactFrequency = computed<string>(() => {
    if (isOutletContact(contact.value)) {
      return (
        contact.value.frequencies
          ?.map((frequency) => frequency.frequency)
          ?.join(", ") || ""
      );
    }

    return "";
  });

  const canShowFullProfile = ref(false);
  const showFullProfile = ref(false);
  const canShowFullBio = ref(false);
  const showFullBio = ref(false);

  const contactProfile = computed(() => {
    if (isOutletContact(contact.value) || isInfluencerContact(contact.value)) {
      return nl2br(contact.value.profile);
    }

    return "";
  });

  const contactPhones = computed<Phone[]>(() => {
    if (isOutletContact(contact.value) || isInfluencerContact(contact.value)) {
      return orderBy(contact.value.phones, "defaultFlag", "desc");
    }

    return [];
  });

  const showAllPhones = ref(false);

  const visibleContactPhones = computed<string[]>(() => {
    const phones = showAllPhones.value
      ? contactPhones.value
      : contactPhones.value.slice(0, 1);

    return phones.map((phone) => {
      const lines = [];
      if (phone.countryCode) lines.push(`+${phone.countryCode}`);
      lines.push(phone.number);

      return lines.join(" ");
    });
  });

  const contactEmails = computed(() => {
    if (!contact.value.emails?.length) return [];

    return orderBy(contact.value.emails, "defaultFlag", "desc");
  });

  const showAllEmails = ref(false);

  const visibleContactEmails = computed(() => {
    if (!contactEmails.value?.length) return [];

    const emails = showAllEmails.value
      ? contactEmails.value
      : contactEmails.value.slice(0, 1);

    return emails.map((email) => email.email);
  });

  const contactOutletType = computed(() => {
    if (isOutletContact(contact.value) && contact.value.outlet_type) {
      return contact.value.outlet_type.name;
    }

    return "";
  });

  const contactCoverage = computed(() => {
    if (isOutletContact(contact.value) && contact.value.outlet_type) {
      return `${contact.value.coverage} ${contact.value.outlet_type.name}`;
    }

    return "";
  });

  const contactOutlets = computed<BaseOutlet[]>(() => {
    // If outlet_names is available as an extra attribute, use it
    if (
      isInfluencerContact(contact.value) &&
      contact.value.outlet_names?.length
    ) {
      const influencer = contact.value;

      return influencer.outlet_names.map(
        (name, index) =>
          ({
            group: name, // fallback for display
            id:
              influencer.influencers_outlets?.[index]?.outlet_id ??
              influencer.outlets?.[index]?.id,
            name,
          }) as BaseOutlet
      );
    }

    // Fallback to existing logic
    if (isInfluencerContact(contact.value)) {
      if (contact.value.outlets?.length) return contact.value.outlets;
    }

    if (
      (isInfluencerContact(contact.value) || isOutletContact(contact.value)) &&
      contact.value.influencers_outlets?.length
    )
      return contact.value.influencers_outlets
        .map((influencersOutlet) => influencersOutlet.outlet)
        .filter((outlet) => outlet !== null);

    if (isPrivateInfluencerContact(contact.value)) {
      return contact.value.private_influencer_public_outlets ?? [];
    }

    return [];
  });

  const contactPrivateOutlets = computed<BasePrivateOutlet[]>(() => {
    if (isPrivateInfluencerContact(contact.value)) {
      return contact.value.private_outlets ?? [];
    }

    return [];
  });

  const contactBio = computed<string>(() => {
    if (isInfluencerContact(contact.value) && contact.value.biography) {
      return contact.value.biography.replaceAll("\n", "<br>");
    }

    return "";
  });

  const twitterBio = computed(() => {
    if (isInfluencerContact(contact.value)) {
      return contact.value.twitter_user
        ? contact.value.twitter_user.profile_description
        : "";
    }

    return "";
  });

  const contactDmaNames = computed(() => {
    if (isOutletContact(contact.value) && contact.value.dma_names) {
      return contact.value.dma_names.join(" / ");
    }

    return "";
  });

  return {
    canShowFullBio,
    canShowFullProfile,
    contactAddresses,
    contactBio,
    contactCirculations,
    contactCirculationTooltip,
    contactClass,
    contactCoverage,
    contactDmaNames,
    contactEmails,
    contactFrequency,
    contactLanguages,
    contactLocation,
    contactOptedOut,
    contactOutlets,
    contactOutletType,
    contactPhones,
    contactPrimaryOutlet,
    contactPrimaryOutletId,
    contactPrimaryOutletName,
    contactPrivateOutlets,
    contactProfile,
    contactRole,
    contactTitle,
    contactTopics,
    isOutlet,
    moreContactTopics,
    moreContactTopicsTooltip,
    showAllAddresses,
    showAllEmails,
    showAllPhones,
    showFullBio,
    showFullProfile,
    sortedContactAddresses,
    twitterBio,
    type,
    visibleContactAddresses,
    visibleContactEmails,
    visibleContactPhones,
    visibleContactTopics,
  };
}
